import { v4 as uuidv4 } from 'uuid';

// Generate service names and types
const serviceNames = [
    'Sound Engineering',
    'Lighting Design',
    'Stage Management',
    'AV Setup',
    'Video Production',
    'Live Streaming',
    'Event Photography',
    'DJ Services',
    'Technical Direction',
    'Production Management',
    'Equipment Rental',
    'Rigging Services',
    'Venue Decoration',
    'Set Construction',
    'Special Effects',
    'Power Distribution',
    'Backline Rental',
    'Projection Mapping',
    'Security Services',
    'Catering Services'
];

const serviceTypes = [
    'Technical',
    'Creative',
    'Management',
    'Logistics',
    'Equipment',
    'Production',
    'Entertainment',
    'Venue',
    'Support',
    'Audio',
    'Visual',
    'Lighting'
];

// Generate service images
const serviceLogos = [
    'https://via.placeholder.com/150/3F51B5/FFFFFF?text=SE',
    'https://via.placeholder.com/150/F44336/FFFFFF?text=LD',
    'https://via.placeholder.com/150/009688/FFFFFF?text=SM',
    'https://via.placeholder.com/150/FF9800/FFFFFF?text=AV',
    'https://via.placeholder.com/150/673AB7/FFFFFF?text=VP',
    'https://via.placeholder.com/150/4CAF50/FFFFFF?text=LS',
    'https://via.placeholder.com/150/2196F3/FFFFFF?text=EP',
    'https://via.placeholder.com/150/FF5722/FFFFFF?text=DJ',
    'https://via.placeholder.com/150/607D8B/FFFFFF?text=TD',
    'https://via.placeholder.com/150/9C27B0/FFFFFF?text=PM',
];

// Generate random date within the last year
const generateRecentDate = () => {
    const now = new Date();
    const pastDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    const randomTime = pastDate.getTime() + Math.random() * (now.getTime() - pastDate.getTime());
    return new Date(randomTime).toISOString().slice(0, 10);
};

// Generate document types
const documentTypes = ['Contract', 'Service Agreement', 'Rate Card', 'Terms of Service', 'Insurance', 'Specifications', 'Technical Rider', 'Sample Files'];

// Generate random documents
const generateDocuments = (count = 3) => {
    const documents = [];
    const documentNames = [
        'Service Agreement',
        'Technical Specifications',
        'Rate Card',
        'Sample Work',
        'Insurance Certificate',
        'Terms and Conditions',
        'Service Portfolio',
        'Client Testimonials',
        'Technical Rider',
        'Equipment List'
    ];

    for (let i = 0; i < Math.min(count, 10); i++) {
        documents.push({
            id: uuidv4(),
            name: documentNames[Math.floor(Math.random() * documentNames.length)],
            date: generateRecentDate(),
            type: documentTypes[Math.floor(Math.random() * documentTypes.length)]
        });
    }

    return documents;
};

// Generate pricing options
const generatePricingOptions = (count = 3) => {
    const pricing = [];
    const pricingNames = [
        'Basic Package',
        'Standard Package',
        'Premium Package',
        'Hourly Rate',
        'Day Rate',
        'Weekend Rate',
        'Event Package',
        'Custom Setup',
        'Equipment Only',
        'Full Service'
    ];

    const units = ['Hour', 'Day', 'Event', 'Project', 'Week', 'Session'];

    for (let i = 0; i < Math.min(count, 10); i++) {
        pricing.push({
            id: uuidv4(),
            name: pricingNames[Math.floor(Math.random() * pricingNames.length)],
            price: `£${(Math.floor(Math.random() * 500) + 50).toFixed(2)}`,
            unit: units[Math.floor(Math.random() * units.length)],
            description: `Complete ${pricingNames[Math.floor(Math.random() * pricingNames.length)].toLowerCase()} with all standard features and support.`
        });
    }

    return pricing;
};

// Generate service providers
const generateProviders = (count = 3) => {
    const providers = [];
    const providerNames = [
        'Elite Audio Services',
        'Spotlight Solutions',
        'Technical Productions Ltd',
        'Creative Vision Media',
        'Sound & Lighting Experts',
        'Stage Management Pros',
        'Events Technical Support',
        'John Smith',
        'Sarah Johnson',
        'Mike Davis',
        'Emily Wilson',
        'Robert Brown',
        'Laura Miller'
    ];

    const providerTypes = ['Company', 'Individual', 'Freelancer', 'Agency', 'Contractor'];
    const phoneFormats = ['+44 7### ######', '07### ######', '+44 20 #### ####', '020 #### ####'];

    for (let i = 0; i < Math.min(count, 10); i++) {
        const name = providerNames[Math.floor(Math.random() * providerNames.length)];
        const type = providerTypes[Math.floor(Math.random() * providerTypes.length)];
        let contactPerson = '';
        if (type === 'Company' || type === 'Agency') {
            const firstNames = ['John', 'Sarah', 'David', 'Emma', 'Michael', 'Jessica'];
            const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Miller'];
            contactPerson = `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`;
        }

        // Generate a random phone number based on the selected format
        const phoneFormat = phoneFormats[Math.floor(Math.random() * phoneFormats.length)];
        const phone = phoneFormat.replace(/#/g, () => Math.floor(Math.random() * 10));

        providers.push({
            id: uuidv4(),
            name,
            type,
            contactPerson,
            phone
        });
    }

    return providers;
};

// Create a fake service
export const createFakeService = (id = null) => {
    const nameIndex = Math.floor(Math.random() * serviceNames.length);
    const typeIndex = Math.floor(Math.random() * serviceTypes.length);

    return {
        id: id || uuidv4(),
        name: serviceNames[nameIndex],
        type: serviceTypes[typeIndex],
        pricePerHour: `£${(Math.floor(Math.random() * 200) + 20).toFixed(2)}`,
        minimumHours: Math.floor(Math.random() * 5) + 1,
        description: `Professional ${serviceNames[nameIndex]} services for events and productions. Experienced staff and high-quality equipment.`,
        isBookable: Math.random() > 0.2,
        isVat: Math.random() > 0.3,
        image: serviceLogos[Math.floor(Math.random() * serviceLogos.length)],
        pricing: generatePricingOptions(Math.floor(Math.random() * 4) + 2),
        documents: generateDocuments(Math.floor(Math.random() * 3) + 1),
        providers: generateProviders(Math.floor(Math.random() * 5) + 1)
    };
};

// Generate multiple fake services
export const generateFakeServices = (count = 10) => {
    const services = [];
    for (let i = 0; i < count; i++) {
        services.push(createFakeService(i + 1));
    }
    return services;
}; 